import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BACKUP_LAG_HOURS, STORAGE_PRESETS, STORAGE_WORM_LABEL, backupHealth, documentStorageLabel, documentStorageTarget,
  type BackupHealth, type BackupStorageInput, type BackupStorageTestResult, type BackupStorageView,
} from '@pqrs/contracts';
import { Icono } from '@xiltrion/ui/icono';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { Plegable } from '@xiltrion/ui/bloques/plegable';
import { Progress } from '@xiltrion/ui/progress';
import { Spinner } from '@xiltrion/ui/spinner';
import { apiDelete, apiGet, apiPost, apiPut } from '@/api/client';
import { useSession } from '@/app/session';
import { Card, ErrorState, KeyValue, LoadingState, PageHeader, fmtDate } from '@/components/ui';
import { errorMessage } from '@/features/cases/lib';
import { SETUP_QUERY_KEY } from '@/features/setup/api';
import { ConfiguredService, notify, notifyError } from './shared';
import { StorageBucketFields, bucketMissing, bucketSettingsOf, initialBucketForm, type BucketForm } from './StorageBucketFields';

const KEY = ['config', 'storage', 'backup'] as const;
/** Mientras se restaura o hay archivos en camino, el estado se refresca solo. */
export const useBackupStorage = () => useQuery({
  queryKey: KEY,
  queryFn: () => apiGet<BackupStorageView>('/storage/backup'),
  refetchInterval: (q) => (q.state.data?.restore?.status === 'RUNNING' ? 5_000 : q.state.data?.status?.pending ? 30_000 : false),
});

/** Sin llave escrita no se manda el campo (se conserva la guardada). */
function bodyOf(f: BucketForm): BackupStorageInput {
  const { wormDefaultDays: _days, ...settings } = bucketSettingsOf(f);
  return { ...settings, ...(f.secret ? { secretAccessKey: f.secret } : {}) };
}

/** Amazon S3 nombra los permisos: los del respaldo no incluyen borrar. */
const PRESET_HINT = { aws: 'Llaves de un usuario de IAM con acceso solo a este bucket (s3:GetObject, PutObject, ListBucket y, con WORM, GetObjectRetention y PutObjectRetention; no necesita DeleteObject). Cree el bucket con Object Lock si quiere WORM.' };

const HEALTH: Record<BackupHealth, { label: string; variant: 'success-light' | 'warning-light' | 'destructive-light' }> = {
  ok: { label: 'Al día', variant: 'success-light' },
  behind: { label: 'Atrasado', variant: 'warning-light' },
  failing: { label: 'Con fallas', variant: 'destructive-light' },
};

/** Configuración › Plataforma › Respaldo. */
export function BackupStoragePage() {
  const q = useBackupStorage();
  return (
    <div>
      <PageHeader title="Respaldo de documentos" subtitle="Una copia de cada archivo del expediente (originales, copias PDF/A, índices firmados, correos recibidos e imágenes) en un bucket de la entidad. El respaldo solo agrega: lo que se borre del almacenamiento principal sigue en él." />
      <div className="space-y-6">
        <BackupBucketCard />
        {q.data?.settings && <BackupStatusCard v={q.data} />}
      </div>
    </div>
  );
}

/** Bucket del respaldo: servicio, bucket, llaves y WORM. */
function BackupBucketCard() {
  const qc = useQueryClient();
  const q = useBackupStorage();
  const [f, setF] = useState<BucketForm>(() => initialBucketForm(null));
  const [removing, setRemoving] = useState(false);
  const [editing, setEditing] = useState(false);
  const v = q.data;
  useEffect(() => { if (v) setF(initialBucketForm(v.settings)); }, [v?.updatedAt, !!v?.settings]); // eslint-disable-line react-hooks/exhaustive-deps
  const saved = v?.settings ?? null;
  /** Con la suite conectada, el respaldo lo asigna Datalogic allá (GUIA-AGENTES §3.12): aquí solo se ve y se prueba. */
  const managed = !!v?.managedBySuite;
  const summary = !!saved && !editing;

  const set = (patch: Partial<BucketForm>) => { setF((x) => ({ ...x, ...patch })); test.reset(); };
  const storedSecretApplies = useMemo(() => {
    if (!saved?.hasSecret) return false;
    try { return documentStorageTarget(saved) === documentStorageTarget(bucketSettingsOf(f)); } catch { return false; }
  }, [saved, f]);
  const missing = bucketMissing(f, !!f.secret || storedSecretApplies);

  const onSaved = (nv: BackupStorageView, msg: string) => { qc.setQueryData(KEY, nv); qc.invalidateQueries({ queryKey: SETUP_QUERY_KEY }); notify('success', msg); };
  const save = useMutation({ mutationFn: () => apiPut<BackupStorageView>('/storage/backup', bodyOf(f)), onSuccess: (nv) => { onSaved(nv, 'Respaldo guardado: los archivos se empiezan a copiar'); setEditing(false); test.reset(); }, onError: notifyError });
  const remove = useMutation({ mutationFn: () => apiDelete<BackupStorageView>('/storage/backup'), onSuccess: (nv) => { onSaved(nv, 'Respaldo quitado'); setEditing(false); }, onError: notifyError });
  const test = useMutation({ mutationFn: () => apiPost<BackupStorageTestResult>('/storage/backup/test', summary ? {} : { config: bodyOf(f) }) });

  if (q.isLoading) return <Card title="Bucket de respaldo"><LoadingState rows={3} /></Card>;
  if (q.isError || !v) return <Card title="Bucket de respaldo"><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>;
  const blocked = v.secretsKey === 'missing';
  const wormMode = summary && saved ? saved.wormMode : f.wormMode;
  const cancelEdit = () => { setF(initialBucketForm(saved)); test.reset(); setEditing(false); };

  const testResult = (<>
    {test.data && (test.data.ok
      ? <Alert variant="success"><Icono nombre="mdi:check-circle" /><AlertTitle>El bucket responde</AlertTitle><AlertDescription>{test.data.detail}{wormMode !== 'off' ? (test.data.wormActive ? ' El bucket tiene Object Lock: el WORM aplica.' : ' El bucket no tiene Object Lock: el WORM no aplicaría.') : ''}{test.data.warnings.map((w) => ` ${w}`).join('')}</AlertDescription></Alert>
      : <Alert variant="destructive"><Icono nombre="mdi:alert-circle-outline" /><AlertTitle>No funcionó</AlertTitle><AlertDescription className="break-words">{test.data.error}</AlertDescription></Alert>)}
    {test.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(test.error)}</AlertDescription></Alert>}
  </>);
  const savedAt = saved && v.updatedAt && <span className="text-xs text-muted-foreground ml-auto">{v.source === 'suite' ? 'Cambiado en la suite' : 'Guardado'} {fmtDate(v.updatedAt)}{v.updatedBy ? ` · ${v.updatedBy}` : ''}</span>;
  const removeButton = saved && !managed && <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setRemoving(true)}><Icono nombre="mdi:trash-can-outline" /> Quitar respaldo</Button>;
  const removeDialog = (
    <ConfirmarDialogo abierto={removing} onAbiertoChange={setRemoving} destructivo titulo="Quitar el respaldo" textoConfirmar="Quitar"
      descripcion="Los archivos nuevos dejan de copiarse. Lo ya copiado se queda en el bucket de la entidad. Las llaves guardadas se borran."
      onConfirmar={() => remove.mutateAsync().then(() => undefined)} />
  );

  const suiteNote = managed && (v.source === 'suite'
    ? <Alert variant="info"><Icono nombre="mdi:apps" /><AlertDescription>El bucket de respaldo se asigna en la suite (ficha de la entidad › Almacenamiento); allí el administrador de la entidad lo puede cambiar.</AlertDescription></Alert>
    : saved
      ? <Alert variant="warning"><Icono nombre="mdi:swap-horizontal" /><AlertTitle>Aún se usa el que tenía SGDEA</AlertTitle><AlertDescription>La suite todavía no le asigna un respaldo a SGDEA. Datalogic lo pasa allá con <code>pnpm suite:entity-export</code>; mientras tanto se sigue copiando a este.</AlertDescription></Alert>
      : <Alert variant="info"><Icono nombre="mdi:apps" /><AlertDescription>La entidad no tiene respaldo. El administrador de la entidad lo asigna en la suite: ficha de la entidad › Almacenamiento, registra el bucket y lo elige como respaldo de SGDEA.</AlertDescription></Alert>);

  if (managed && !(summary && saved)) return <Card title="Bucket de respaldo">{suiteNote}</Card>;

  if (summary && saved) {
    const host = saved.endpoint ? saved.endpoint.replace(/^https?:\/\//, '') : `Amazon S3 (${saved.region})`;
    return (
      <Card title="Bucket de respaldo">
        <ConfiguredService icon="mdi:backup-restore" title={<>Bucket {saved.bucket}{saved.prefix ? `/${saved.prefix}` : ''} en {STORAGE_PRESETS[saved.preset].label}</>}
          items={[
            { label: 'Servicio', value: host },
            { label: 'Región', value: saved.region },
            { label: 'Llave de acceso', value: saved.accessKeyId },
            { label: 'WORM', value: saved.wormMode === 'off' ? STORAGE_WORM_LABEL.off : <span className="inline-flex items-center gap-2">{STORAGE_WORM_LABEL[saved.wormMode]} <Badge variant={v.wormActive ? 'success-light' : 'warning-light'} size="sm">{v.wormActive ? 'Activo' : 'Sin Object Lock'}</Badge></span> },
          ]}
          actions={<>
            {!managed && <Button type="button" size="sm" variant="outline" onClick={() => { test.reset(); setEditing(true); }}><Icono nombre="mdi:pencil-outline" /> Editar</Button>}
            <Button type="button" size="sm" variant="outline" disabled={test.isPending} onClick={() => test.mutate()}>{test.isPending ? <Spinner /> : <Icono nombre="mdi:lan-connect" />} Probar conexión</Button>
            {removeButton}
            {savedAt}
          </>}>
          {suiteNote}
          {testResult}
        </ConfiguredService>
        {removeDialog}
      </Card>
    );
  }

  return (
    <Card title="Bucket de respaldo">
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (!missing) save.mutate(); }}>
        {blocked && <Alert variant="destructive"><Icono nombre="mdi:key-outline" /><AlertTitle>El servidor no tiene SECRETS_KEY</AlertTitle><AlertDescription>Sin la llave maestra no se pueden guardar llaves de acceso. Hay que configurarla en el servidor.</AlertDescription></Alert>}
        {!saved && <p className="text-sm text-muted-foreground m-0">La entidad todavía no tiene respaldo. Al guardarlo se copian todos los archivos que ya existen y, desde ahí, cada archivo nuevo apenas se guarda.</p>}
        {saved && <Alert variant="info"><Icono nombre="mdi:information-outline" /><AlertDescription>Si cambia el servicio, el bucket o la carpeta, todos los archivos se copian de nuevo al bucket nuevo. Lo copiado al anterior se queda allí.</AlertDescription></Alert>}
        <StorageBucketFields id="bk" f={f} set={set} storedSecretApplies={storedSecretApplies} presetHint={PRESET_HINT}
          wormHint="Con Object Lock en el bucket, cada copia queda protegida con la misma retención que el original." />

        {testResult}
        {missing && <p className="text-xs text-muted-foreground m-0">Falta {missing}.</p>}
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm" disabled={!!missing || save.isPending || blocked}>{save.isPending ? <Spinner /> : <Icono nombre="mdi:check" />} Guardar</Button>
          {editing && <Button type="button" size="sm" variant="ghost" onClick={cancelEdit}>Cancelar</Button>}
          <Button type="button" size="sm" variant="outline" disabled={!!missing || test.isPending} onClick={() => test.mutate()}>{test.isPending ? <Spinner /> : <Icono nombre="mdi:lan-connect" />} Probar conexión</Button>
          {removeButton}
          {savedAt}
        </div>
      </form>
      {removeDialog}
    </Card>
  );
}

/** Cómo va la copia: cuántos archivos, cuántos faltan, la última copia y el último error. */
function BackupStatusCard({ v }: { v: BackupStorageView }) {
  const qc = useQueryClient();
  const retry = useMutation({ mutationFn: () => apiPost<BackupStorageView>('/storage/backup/retry', {}), onSuccess: (nv) => { qc.setQueryData(KEY, nv); qc.invalidateQueries({ queryKey: SETUP_QUERY_KEY }); notify('success', 'Se vuelven a intentar los archivos fallidos'); }, onError: notifyError });
  const st = v.status;
  if (!st) return null;
  const health = HEALTH[backupHealth(st)];
  const pct = st.total ? Math.floor((st.copied / st.total) * 100) : 100;
  return (
    <Card title="Estado del respaldo" actions={<Badge variant={health.variant} size="sm">{health.label}</Badge>}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between text-sm"><span><span className="tnum font-medium">{st.copied}</span> de <span className="tnum">{st.total}</span> archivos copiados</span><span className="tnum text-muted-foreground">{pct} %</span></div>
          <Progress value={pct} aria-label="Archivos copiados" />
        </div>
        <KeyValue items={[
          { label: 'En camino', value: <span className="tnum">{st.pending}</span> },
          { label: 'Fallidos', value: <span className={`tnum ${st.failed ? 'text-destructive' : ''}`}>{st.failed}</span> },
          { label: 'Última copia', value: fmtDate(st.lastCopyAt) },
          ...(st.oldestPendingAt ? [{ label: 'Pendiente desde', value: fmtDate(st.oldestPendingAt) }] : []),
        ]} />
        {st.pending > 0 && backupHealth(st) === 'ok' && <p className="text-xs text-muted-foreground m-0">Los archivos nuevos se copian apenas se guardan; lo demás, en tandas cada minuto. Si algo lleva más de {BACKUP_LAG_HOURS} horas sin copiarse, el respaldo se marca atrasado.</p>}
        {st.lastError && (st.failed > 0 || backupHealth(st) !== 'ok') && (
          <Alert variant={st.failed ? 'destructive' : 'warning'}><Icono nombre="mdi:alert-circle-outline" />
            <AlertTitle>Último error{st.lastErrorAt ? ` · ${fmtDate(st.lastErrorAt)}` : ''}</AlertTitle>
            <AlertDescription className="break-words">{st.lastError}</AlertDescription>
          </Alert>
        )}
        {st.failed > 0 && (
          <div><Button type="button" size="sm" variant="outline" disabled={retry.isPending} onClick={() => retry.mutate()}>{retry.isPending ? <Spinner /> : <Icono nombre="mdi:refresh" />} Reintentar fallidos</Button></div>
        )}
      </div>
    </Card>
  );
}

/**
 * Configuración › Almacenamiento (solo la plataforma): el respaldo de la entidad visto desde el principal, y
 * restaurar desde él lo que falte.
 */
export function BackupRestoreCard() {
  const qc = useQueryClient();
  const canAdvanced = useSession().can('admin.advanced');
  const q = useBackupStorage();
  const [confirming, setConfirming] = useState(false);
  const restore = useMutation({ mutationFn: () => apiPost<BackupStorageView>('/storage/backup/restore', {}), onSuccess: (nv) => { qc.setQueryData(KEY, nv); notify('success', 'Restauración iniciada'); }, onError: notifyError });
  if (!canAdvanced) return null;
  if (q.isLoading) return <Card title="Respaldo de la entidad"><LoadingState rows={2} /></Card>;
  if (q.isError || !q.data) return <Card title="Respaldo de la entidad"><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>;
  const v = q.data;
  if (!v.settings || !v.status) {
    return <Card title="Respaldo de la entidad"><p className="text-sm text-muted-foreground m-0">La entidad no ha configurado un respaldo (Configuración › Respaldo).</p></Card>;
  }
  const health = HEALTH[backupHealth(v.status)];
  const r = v.restore;
  const running = r?.status === 'RUNNING';
  const done = r ? r.restored + r.present + r.failed : 0;
  return (
    <Card title="Respaldo de la entidad" actions={<Badge variant={health.variant} size="sm">{health.label}</Badge>}>
      <div className="flex flex-col gap-4">
        <p className="text-sm m-0">Copia en el {documentStorageLabel(v.settings)}: <span className="tnum">{v.status.copied}</span> de <span className="tnum">{v.status.total}</span> archivos{v.status.failed ? <>, <span className="tnum text-destructive">{v.status.failed}</span> fallidos</> : ''}.</p>
        {r && (
          <Alert variant={running ? 'info' : r.failed ? 'warning' : 'success'}><Icono nombre={running ? 'mdi:progress-download' : r.failed ? 'mdi:alert-outline' : 'mdi:check-circle'} />
            <AlertTitle>{running ? 'Restaurando desde el respaldo' : `Restauración terminada ${fmtDate(r.finishedAt)}`}</AlertTitle>
            <AlertDescription>
              <div className="flex flex-col gap-2 w-full">
                <span>Iniciada {fmtDate(r.startedAt)}{r.startedBy ? ` por ${r.startedBy}` : ''}. Revisados <span className="tnum">{done}</span> de <span className="tnum">{r.total}</span>: <span className="tnum">{r.restored}</span> traídos de vuelta, <span className="tnum">{r.present}</span> ya estaban, <span className="tnum">{r.failed}</span> fallidos.</span>
                {running && <Progress value={r.total ? Math.floor((done / r.total) * 100) : 0} aria-label="Archivos revisados" />}
                {r.errors.length > 0 && (
                  <Plegable titulo={`Fallidos (${r.errors.length})`}>
                    <ul className="m-0 pl-5 space-y-1 text-xs">{r.errors.map((e, i) => <li key={`${e.key}-${i}`} className="break-all">{e.key ? `${e.key}: ` : ''}{e.error}</li>)}</ul>
                  </Plegable>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" variant="outline" disabled={running || restore.isPending} onClick={() => setConfirming(true)}>{restore.isPending ? <Spinner /> : <Icono nombre="mdi:backup-restore" />} Restaurar desde el respaldo</Button>
          <span className="text-xs text-muted-foreground">Trae al almacenamiento principal los archivos que falten, verificando su huella. No reemplaza los que ya están.</span>
        </div>
      </div>
      <ConfirmarDialogo abierto={confirming} onAbiertoChange={setConfirming} titulo="Restaurar desde el respaldo" textoConfirmar="Restaurar"
        descripcion={`Se revisan los ${v.status.copied} archivos copiados al ${documentStorageLabel(v.settings)} y se traen al almacenamiento principal los que falten. Avanza en segundo plano.`}
        onConfirmar={() => restore.mutateAsync().then(() => undefined)} />
    </Card>
  );
}
